import { DialogHelper } from '@juvare/common-web-ui/DialogHelper';
import { JuvareLogoFull } from '@juvare/common-web-ui/JuvareLogoFull';
import { createJuvareAppMenuTheme, createJuvareTheme } from '@juvare/common-web-ui/JuvareTheme';
import { SinglePageApp } from '@juvare/common-web-ui/SinglePageApp';
import { SnackbarHelper } from '@juvare/common-web-ui/SnackbarHelper';
import { AppContextProvider } from '@juvare/platform-sdk-react/AppContext';
import { AppRootWithRouter } from '@juvare/platform-sdk-react/AppRootWithRouter';
import { AuthenticatedAppLoader } from '@juvare/platform-sdk-react/AuthenticatedAppLoader';
import { useNotifyClient, useUserClient } from '@juvare/platform-sdk-react/Hooks';
import { Avatar, Box, Button, ThemeProvider, Typography } from '@mui/material';
import React from 'react';
import { Route, Routes, useNavigate } from 'react-router';
import { InboxConfigProvider } from '../../src/InboxConfigProvider';
import { UserState } from '../backend/app';
import { AppContext } from './Context';
import { DocPage, DocPageProps } from './DocPage';
import { Home } from './Home';

const pages: (DocPageProps & { path: string })[] = [];
const appTheme = createJuvareTheme();
const menuTheme = createJuvareAppMenuTheme();

export const App: React.FC = () => {
    return (
        <AppRootWithRouter>
            <AppContextProvider appContext={AppContext}>
                <ThemeProvider theme={appTheme}>
                    <DialogHelper />
                    <SnackbarHelper />
                    <AppLoader />
                </ThemeProvider>
            </AppContextProvider>
        </AppRootWithRouter>
    );
};

const AppLoader: React.FC = () => {
    return (
        <AuthenticatedAppLoader
            appContext={AppContext}
            app='inboxUI_doc'
            loadingFallback={() => <div>Loading...</div>}
            authenticated={(user) => <User user={user} />}
            anonymous={(loginUrl) => <Login loginUrl={loginUrl} />}
        />
    );
};

const User: React.FC<{ user: UserState }> = ({ user }) => {
    const navigate = useNavigate();
    const userClient = useUserClient();
    const notifyClient = useNotifyClient();
    const appContext = React.useContext(AppContext);
    const [loginEmail] = React.useState(appContext.currentUser?.user ?? '');

    return (
        <InboxConfigProvider
            baseUrl='/platform/proxy/notify/inbox/api'
            onTokenRequest={() => notifyClient.getInboxToken().then((response) => response.token)}
            loginEmail={loginEmail}
        >
            <SinglePageApp
                title={`Documentation - ${process.env.PROJECT_NAME} ${process.env.PROJECT_VERSION}`}
                appLogo={<Avatar>IN</Avatar>}
                vendorLogo={<JuvareLogoFull />}
                pages={[
                    { label: 'Home', onSelect: () => navigate('/') },
                    {
                        label: 'Components',
                        subPages: pages.map((page) => ({
                            label: page.name,
                            onSelect: () => {
                                navigate(page.path);
                            },
                        })),
                        expanded: true,
                    },
                ]}
                toolbar={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box mr={1}>{user.displayName}</Box>
                        <Button color='primary' onClick={() => userClient.logout().finally(() => appContext.initState())}>
                            Log Out
                        </Button>
                    </Box>
                }
                menuTheme={menuTheme}
            >
                <Routes>
                    <Route path='/' element={<Home pages={pages} />} />
                    {pages.map((page) => (
                        <Route key={page.path} path={page.path + '/*'} element={<DocPage {...page} />} />
                    ))}
                </Routes>
            </SinglePageApp>
        </InboxConfigProvider>
    );
};

const Login: React.FC<{ loginUrl: string }> = ({ loginUrl }) => {
    return (
        <Box p={2}>
            <Typography variant='h4' sx={{ mb: 2 }}>
                {`Documentation - ${process.env.PROJECT_NAME} ${process.env.PROJECT_VERSION}`}
            </Typography>
            <Button
                variant='contained'
                color='primary'
                onClick={() => {
                    window.location.assign(loginUrl);
                }}
            >
                Login
            </Button>
        </Box>
    );
};

export function include(page: DocPageProps): void {
    pages.push({ ...page, path: '/' + page.name.toLowerCase() });
}
